import React from "react";
import type { FileRecord } from "../../services/filesService";

interface Props {
  file: FileRecord;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Standalone delete confirmation (not the packages/excalidraw ConfirmDialog,
 * which depends on the Excalidraw editor context this page doesn't render
 * in). Self-contained modal, matching the app's existing dialog styling.
 */
export const DeleteConfirmDialog: React.FC<Props> = ({
  file,
  busy,
  onConfirm,
  onCancel,
}) => {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "var(--mf-overlay)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 100,
      }}
      onClick={onCancel}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-label="Delete mindmap"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--mf-surface)",
          color: "var(--mf-text)",
          borderRadius: "0.5rem",
          boxShadow: "var(--mf-shadow-lg)",
          padding: "1.5rem",
          width: "min(90vw, 26rem)",
        }}
      >
        <h2 style={{ margin: "0 0 0.75rem", fontSize: "1.1rem" }}>
          Delete mindmap?
        </h2>
        <p
          data-testid="delete-confirm-message"
          style={{
            margin: "0 0 1.25rem",
            color: "var(--mf-text-muted)",
          }}
        >
          Delete &lsquo;
          <span
            data-testid="delete-confirm-title"
            title={file.title}
            style={{
              display: "inline-block",
              maxWidth: "80%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              // `overflow: hidden` makes this inline-block's baseline its
              // bottom margin edge, so a plain baseline alignment lifts the
              // title above the surrounding sentence; text-bottom puts it back
              // on the same visual line.
              verticalAlign: "text-bottom",
            }}
          >
            {file.title}
          </span>
          &rsquo;? You can&apos;t undo this yet.
        </p>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "0.5rem",
          }}
        >
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            style={{
              padding: "0.5rem 1rem",
              borderRadius: "0.375rem",
              border: "1px solid var(--mf-control-border)",
              background: "transparent",
              color: "var(--mf-text)",
              cursor: busy ? "default" : "pointer",
              fontSize: "0.9rem",
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            style={{
              padding: "0.5rem 1rem",
              borderRadius: "0.375rem",
              border: "none",
              background: "var(--mf-danger-solid)",
              color: "var(--mf-danger-solid-text)",
              cursor: busy ? "default" : "pointer",
              fontSize: "0.9rem",
              opacity: busy ? 0.7 : 1,
            }}
          >
            {busy ? "Deleting…" : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteConfirmDialog;
